/**
 * Defensive coercion of an axios error into a human-readable string.
 *
 * FastAPI quirk: HTTPException raises return `detail` as a string, but
 * Pydantic validation failures (422) return `detail` as an array of
 * {loc, msg, type} objects. Passing the array straight to a renderer
 * (sonner toast, JSX) throws React error #31 and blanks the page.
 *
 * Always pipe API errors through this before rendering / toasting.
 *
 * @param {*} error  Caught error (from axios, fetch, or anywhere)
 * @param {string} fallback  Message to use when nothing parseable found
 * @returns {string}  Always a string, never an object or array
 */
export function getErrorMessage(error, fallback = 'Something went wrong') {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map(d => (d && typeof d === 'object' ? d.msg : null))
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  if (detail && typeof detail === 'object') {
    if (typeof detail.msg === 'string') return detail.msg;
    try {
      return JSON.stringify(detail);
    } catch {
      // fall through to fallback
    }
  }

  // Some endpoints return a top-level `message` instead of `detail`
  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) return message;

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return fallback;
}
